import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import KeyIcon from '@mui/icons-material/Key'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import DataSet from 'common-ui/components/DataSet'
import { formatApplianceCodecCapacity } from 'common/applianceCodecLicense'
import { formatApplianceLicenseComponents } from 'common/applianceLicense'

import { ApplianceDetails, ApplianceLicenseBundle, Customer, Role } from 'common-billing-server/types'

import { Banner } from 'common-ui/components/Banner'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useBillingApi } from '../../api/billing/billing-context'
import { formatDate, formatDateTime } from '../../format-date'
import { makeEditCustomerPath } from '../../routes/path'
import { ActivationKey } from '../common/ActivationKey'
import { AutoComplete } from '../common/AutoComplete'
import { RoleBasedComponent, RoleBasedLink } from '../common/RoleBasedLink'
import Wrapper from '../common/Wrapper'
import { SshAccessDialog } from './SshAccessDialog'

const safeFilenamePart = (value: string) => value.replace(/[^a-zA-Z0-9._-]+/g, '-')

/** Downloads the signed token accepted by the appliance's offline license flow. */
export const downloadApplianceLicenseBundle = (bundle: ApplianceLicenseBundle) => {
    const blob = new Blob([`${bundle.token}\n`], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `nimbra-license-${safeFilenamePart(bundle.serialNumber)}-v${bundle.version}.txt`
    link.click()
    URL.revokeObjectURL(url)
}

const formatLicenseEntitlement = (license: ApplianceDetails['licenses'][number]): string => {
    if (license.components) return formatApplianceLicenseComponents(license.components).join(', ')
    if (license.codecCapacity) return formatApplianceCodecCapacity(license.codecCapacity)
    if (license.entitlementMetric === 'featureFlag') return 'Enabled'
    if (license.entitlementMetric === 'unlimitedFlows') return 'Maximum flows'
    const unit = license.entitlementMetric === 'flows' ? 'flow' : 'channel'
    return `${license.entitlementValue.toLocaleString()} ${unit}${license.entitlementValue === 1 ? '' : 's'}`
}

export const ApplianceDetailsPage = () => {
    const { serialNumber } = useParams()
    const billingApi = useBillingApi()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [notice, setNotice] = useState<string>()
    const [appliance, setAppliance] = useState<ApplianceDetails>()
    const [loading, setLoading] = useState(true)
    const [licenseActionLoading, setLicenseActionLoading] = useState(false)
    const [isSshAccessDialogOpen, setIsSshAccessDialogOpen] = useState(false)
    const [isOwnershipDialogOpen, setIsOwnershipDialogOpen] = useState(false)
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
    const [ownershipLoading, setOwnershipLoading] = useState(false)

    const load = useCallback(async () => {
        if (!serialNumber) {
            addFailedOperation({ id: 'load-appliance', message: 'The appliance serial number is missing.' })
            setLoading(false)
            return
        }
        removeFailedOperation('load-appliance')
        setLoading(true)
        try {
            setAppliance(await billingApi.getAppliance(encodeURIComponent(serialNumber)))
        } catch {
            addFailedOperation({
                id: 'load-appliance',
                message: 'The appliance could not be loaded. It may no longer exist.',
                retryFn: () => void load(),
            })
        } finally {
            setLoading(false)
        }
        // The failed-operation helpers are recreated each render; listing them here would re-run load forever
    }, [billingApi, serialNumber])

    useEffect(() => {
        void load()
    }, [load])

    const getCurrentBundle = async () => {
        if (!serialNumber) throw new Error('The appliance serial number is missing.')
        const result = await billingApi.listApplianceLicenseBundles({
            filter: { serialNumber },
            limit: 1,
        })
        const bundle = result.items[0]
        if (!bundle) throw new Error(`No active license bundle exists for ${serialNumber}.`)
        return bundle
    }

    const copyLicense = async () => {
        setLicenseActionLoading(true)
        try {
            const bundle = await getCurrentBundle()
            await navigator.clipboard.writeText(bundle.token)
            setNotice(`Copied license bundle (v${bundle.version}) for ${bundle.serialNumber}.`)
        } catch {
            addFailedOperation({
                id: 'license-action',
                message: 'The appliance license could not be copied. Please try again.',
            })
        } finally {
            setLicenseActionLoading(false)
        }
    }

    const downloadLicense = async () => {
        setLicenseActionLoading(true)
        try {
            const bundle = await getCurrentBundle()
            downloadApplianceLicenseBundle(bundle)
            setNotice(`Downloaded license bundle (v${bundle.version}) for ${bundle.serialNumber}.`)
        } catch {
            addFailedOperation({
                id: 'license-action',
                message: 'The appliance license could not be downloaded. Please try again.',
            })
        } finally {
            setLicenseActionLoading(false)
        }
    }

    const licenseActionsDisabled =
        loading ||
        !appliance ||
        licenseActionLoading ||
        !appliance.licenses.some(({ licenseFamily }) => licenseFamily !== 'flow')
    // Only standalone licenses carry a key, so leave the column out when no license on the serial has one
    const hasActivationKeys = appliance?.licenses.some((license) => license.activationKey) ?? false

    const openSshAccessDialog = () => {
        removeFailedOperation('load-ssh-key')
        removeFailedOperation('ssh-certificate')
        setIsSshAccessDialogOpen(true)
    }

    const closeOwnershipDialog = () => {
        setIsOwnershipDialogOpen(false)
        setSelectedCustomer(null)
    }

    const transferOwnership = async () => {
        if (!serialNumber || !appliance || !selectedCustomer) return

        const previousCustomer = appliance.customer.name
        removeFailedOperation('transfer-appliance-ownership')
        setOwnershipLoading(true)
        try {
            const updatedAppliance = await billingApi.setApplianceCustomer(encodeURIComponent(serialNumber), {
                customerId: selectedCustomer.id,
            })
            setAppliance(updatedAppliance)
            setNotice(`Transferred ${serialNumber} from ${previousCustomer} to ${selectedCustomer.name}.`)
            closeOwnershipDialog()
        } catch (error) {
            addFailedOperation({
                id: 'transfer-appliance-ownership',
                message: 'The appliance ownership could not be transferred.',
                error: error as Error,
            })
        } finally {
            setOwnershipLoading(false)
        }
    }

    return (
        <Wrapper
            title={serialNumber ?? 'Appliance details'}
            actions={
                <Stack direction="row" spacing={1}>
                    <RoleBasedComponent minimumAllowedRole={Role.admin}>
                        <Button
                            variant="outlined"
                            startIcon={<KeyIcon />}
                            onClick={openSshAccessDialog}
                            disabled={loading || !appliance}
                        >
                            Get SSH access
                        </Button>
                    </RoleBasedComponent>
                    <RoleBasedComponent minimumAllowedRole={Role.licenseAdmin}>
                        <Button
                            variant="outlined"
                            startIcon={<SwapHorizIcon />}
                            onClick={() => setIsOwnershipDialogOpen(true)}
                            disabled={loading || !appliance}
                        >
                            Transfer ownership
                        </Button>
                    </RoleBasedComponent>
                    <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => void copyLicense()}
                        disabled={licenseActionsDisabled}
                    >
                        Copy license
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => void downloadLicense()}
                        disabled={licenseActionsDisabled}
                    >
                        Download license
                    </Button>
                </Stack>
            }
        >
            {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
                    <CircularProgress aria-label="Loading appliance details" />
                </Box>
            ) : appliance ? (
                <Stack spacing={2}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h2" sx={{ mb: 2 }}>
                            Appliance
                        </Typography>
                        <DataSet
                            values={{
                                'Serial number': appliance.serialNumber,
                                SKU: appliance.productSku ?? '—',
                                'Purchase date (UTC)': appliance.purchasedAt ? formatDate(appliance.purchasedAt) : '—',
                                Customer: (
                                    <RoleBasedLink
                                        minimumAllowedRole={Role.admin}
                                        to={makeEditCustomerPath(appliance.customer.id)}
                                    >
                                        {appliance.customer.name}
                                    </RoleBasedLink>
                                ),
                            }}
                        />
                    </Paper>

                    <Paper sx={{ overflow: 'hidden' }}>
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h2">Active licenses</Typography>
                        </Box>
                        <TableContainer>
                            <Table aria-label="Active appliance licenses">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>License</TableCell>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Family</TableCell>
                                        <TableCell>Entitlement</TableCell>
                                        <TableCell>Allocated (UTC)</TableCell>
                                        {hasActivationKeys && <TableCell>Activation key</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {appliance.licenses.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>No active licenses</TableCell>
                                        </TableRow>
                                    ) : (
                                        appliance.licenses.map((license) => (
                                            <TableRow key={license.allocationId}>
                                                <TableCell component="th" scope="row">
                                                    {license.productName}
                                                </TableCell>
                                                <TableCell>{license.productCode}</TableCell>
                                                <TableCell>{license.licenseFamily}</TableCell>
                                                <TableCell>{formatLicenseEntitlement(license)}</TableCell>
                                                <TableCell>{formatDateTime(license.allocatedAt)}</TableCell>
                                                {hasActivationKeys && (
                                                    <TableCell>
                                                        {license.activationKey ? (
                                                            <ActivationKey value={license.activationKey} />
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Stack>
            ) : null}
            {errorBanner}
            {notice && <Banner id="notice" isOpen={true} message={notice} onClose={() => setNotice(undefined)} />}
            <SshAccessDialog
                open={isSshAccessDialogOpen}
                serialNumber={serialNumber}
                onClose={() => setIsSshAccessDialogOpen(false)}
                onIssued={(certificate) =>
                    setNotice(
                        `Downloaded an SSH certificate valid until ${formatDateTime(certificate.validUntilDate)}.`
                    )
                }
                onFailed={addFailedOperation}
            />
            <Dialog
                open={isOwnershipDialogOpen}
                onClose={ownershipLoading ? undefined : closeOwnershipDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Transfer appliance ownership</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                        Transfer {appliance?.serialNumber} and all its active licenses from {appliance?.customer.name}{' '}
                        to another customer.
                    </DialogContentText>
                    <AutoComplete<Customer>
                        placeholder="New customer"
                        value={selectedCustomer}
                        onValueSelected={setSelectedCustomer}
                        formatSelectedValue={(customer) => customer.name}
                        isOptionDisabled={(customer) => customer.id === appliance?.customer.id}
                        isClearable
                        isRequired
                        isDisabled={ownershipLoading}
                        api={(query) => billingApi.listCustomers({ ...query, filter: { searchName: query.filter } })}
                        dataTestId="appliance-owner"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeOwnershipDialog} disabled={ownershipLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void transferOwnership()}
                        disabled={
                            !selectedCustomer || selectedCustomer.id === appliance?.customer.id || ownershipLoading
                        }
                    >
                        {ownershipLoading ? 'Transferring…' : 'Transfer ownership'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Wrapper>
    )
}
